"use client";

import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Wrapper, Toggle, Submenu, ChildListItem, ListItem } from "./sidebar-menu.styles";
import { Option, type OptionProps } from "../option/option";
import { Tooltip } from "../tooltip/tooltip";
import { Button } from "../button/button";
import { Arrow } from "../arrow/arrow";
import { OptionGroup } from "../option/option-group/option-group";
import { Popover } from "../popover/popover";

export interface SidebarMenuProps {
    /**
     * Whether the sidebar menu should be expanded or not.
     */
    expanded?: boolean;

    /**
     * The index or indices of the active item(s).
     */
    active?: number | number[];

    /**
     * Whether sibling items should close when toggling an item.
     */
    closeSiblingsOnToggle?: boolean;

    /**
     * Sidebar menu items
     */
    items: (OptionProps & { children?: OptionProps[] })[];

    /**
     * Class name of the sidebar menu.
     */
    className?: string;
}

export const SidebarMenu: FunctionComponent<SidebarMenuProps> = ({ expanded, items, active, closeSiblingsOnToggle = true, className }) => {
    const [activeKeys, setActiveKeys] = useState<number[]>([]);
    const [popoverOpen, setPopoverOpen] = useState<number>(-1);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const submenuRefs = useRef<Record<number, HTMLUListElement | null>>({});
    const allItemKeys = items.map((_, index) => index);

    useEffect(() => {
        if (active === undefined) return;
        const initialKeys = Array.isArray(active) ? active : [active];
        const normalizedKeys = closeSiblingsOnToggle ? initialKeys.slice(0, 1) : initialKeys;
        const validKeys = normalizedKeys.filter((key) => allItemKeys.includes(key));
        setIsAnimating(true);
        allItemKeys.forEach((itemKey) => {
            toggleItem(itemKey, validKeys.includes(itemKey));
        });
        setActiveKeys(validKeys);
    }, [active, closeSiblingsOnToggle]);

    useEffect(() => {
        allItemKeys.forEach((itemKey) => {
            toggleItem(itemKey, false);
        });
    }, [expanded]);

    function onToggleItem(key: number) {
        if (isAnimating || !expanded) return;
        const isOpen = activeKeys.includes(key);
        setIsAnimating(true);
        let newActiveKeys: number[];
        if (closeSiblingsOnToggle) {
            newActiveKeys = isOpen ? [] : [key];
        } else {
            newActiveKeys = isOpen ? activeKeys.filter((k) => k !== key) : [...activeKeys, key];
        }
        const changedKeys = new Set([...activeKeys, ...newActiveKeys]);
        changedKeys.forEach((itemKey) => {
            const wasOpen = activeKeys.includes(itemKey);
            const willBeOpen = newActiveKeys.includes(itemKey);
            if (wasOpen !== willBeOpen) {
                toggleItem(itemKey, willBeOpen);
            }
        });
        setActiveKeys(newActiveKeys);
    }

    function toggleItem(key: number, open: boolean) {
        const submenu = submenuRefs.current[key];
        if (!submenu) return;

        if (open) {
            const children = Array.from(submenu.children) as HTMLElement[];
            submenu.style.transition = "none";
            submenu.style.visibility = "hidden";
            submenu.style.position = "absolute";
            submenu.style.height = "auto";
            submenu.style.pointerEvents = "none";

            const fullHeight = submenu.scrollHeight + "px";

            submenu.style.height = "0px";
            submenu.style.removeProperty("visibility");
            submenu.style.removeProperty("position");
            submenu.style.removeProperty("pointer-events");
            submenu.style.removeProperty("transition");

            requestAnimationFrame(() => {
                submenu.style.height = fullHeight;

                children.forEach((child) => {
                    child.style.transform = "translateY(0)";
                });

                const onTransitionEnd = (e: TransitionEvent) => {
                    if (e.propertyName === "height") {
                        submenu.style.height = "auto";
                        submenu.removeEventListener("transitionend", onTransitionEnd);
                        setIsAnimating(false);
                    }
                };

                submenu.addEventListener("transitionend", onTransitionEnd);
            });
        } else {
            setActiveKeys([]);
            const currentHeight = submenu.scrollHeight + "px";
            const children = Array.from(submenu.children) as HTMLElement[];
            submenu.style.height = currentHeight;

            requestAnimationFrame(() => {
                submenu.style.height = "0px";
                children.forEach((child) => {
                    child.style.removeProperty("transform");
                });
            });

            const onTransitionEnd = (e: TransitionEvent) => {
                if (e.propertyName === "height") {
                    submenu.removeEventListener("transitionend", onTransitionEnd);
                    setIsAnimating(false);
                }
            };

            submenu.addEventListener("transitionend", onTransitionEnd);
        }
    }

    function onOpenPopover(key: number) {
        setPopoverOpen(key);
    }

    return (
        <Wrapper className={`group ${className || ""}`.trim()} onMouseLeave={() => setPopoverOpen(-1)}>
            {items.map((item, index) => {
                const { color = "white", rounded = "md", size, leftAdornments, disabled, onClick, link } = item;
                const buttonProps = {
                    color: activeKeys.includes(index) && expanded ? "primary" : color,
                    rounded,
                    size,
                    leftAdornments,
                    disabled,
                    onClick,
                    link,
                };

                return (
                    <ListItem key={index}>
                        {expanded ? (
                            <Option
                                rounded="lg"
                                color={activeKeys.includes(index) ? "primary" : undefined}
                                {...item}
                                onClick={(event) => {
                                    item.onClick?.(event);
                                    if (item.children) {
                                        onToggleItem(index);
                                    }
                                }}
                                rightAdornments={
                                    item.children ? (
                                        <Toggle open={activeKeys.includes(index)}>
                                            <Arrow />
                                        </Toggle>
                                    ) : (
                                        item.rightAdornments
                                    )
                                }
                            />
                        ) : item.children ? (
                            <Popover
                                open={popoverOpen === index}
                                list={true}
                                onToggle={(open) => {
                                    if (!open) {
                                        setPopoverOpen(-1);
                                    }
                                }}
                                button={
                                    <Button
                                        {...buttonProps}
                                        onClick={(event) => {
                                            item.onClick?.(event);
                                            if (item.children) {
                                                onToggleItem(index);
                                            }
                                        }}
                                        onMouseEnter={() => onOpenPopover(index)}
                                    />
                                }
                            >
                                <OptionGroup label={item.label}>
                                    {item.children.map((child, childIndex) => (
                                        <Option rounded="lg" {...child} key={childIndex} />
                                    ))}
                                </OptionGroup>
                            </Popover>
                        ) : (
                            <Tooltip text={item.label} placement="right-center" theme="standalone">
                                <div>
                                    <Button
                                        {...buttonProps}
                                        onClick={(event) => {
                                            item.onClick?.(event);
                                            if (item.children) {
                                                onToggleItem(index);
                                            }
                                        }}
                                        onMouseEnter={() => setPopoverOpen(-1)}
                                    />
                                </div>
                            </Tooltip>
                        )}
                        {item.children && expanded && (
                            <Submenu
                                ref={(el) => {
                                    submenuRefs.current[index] = el;
                                }}
                            >
                                {item.children.map((child, childIndex) => (
                                    <ChildListItem key={childIndex}>
                                        <Option rounded="lg" leftAdornments={<div></div>} {...child} />
                                    </ChildListItem>
                                ))}
                            </Submenu>
                        )}
                    </ListItem>
                );
            })}
        </Wrapper>
    );
};
